import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  cancelDataImport,
  confirmScope3AfImport,
  createCat8ManualInput,
  createDataImport,
  createSeatManualInput,
  deleteDataInput,
  getActiveDataImports,
  getDataInputDriverHistory,
  getDataInputImportHistory,
  getDataInputOverview,
  getDataRecalculationReadiness,
  getDataRecalculationHistory,
  getDriverSnapshot,
  getScope3AfSnapshot,
  runDataRecalculation,
  saveCat19Outsourcing,
  syncCat8Driver,
  syncSeatDriver,
} from '../../../api/dataInputApi';
import {
  getAuthenticatedUserId,
  useAuthContext,
} from '../../../context/AuthContext';
import createIdempotencyKey from '../../../shared/utils/idempotency';
import { reportingCalendarYear } from '../../../shared/utils/reportingYear';
import {
  canCancelKnownImport,
  isActiveImportStatus,
  isTerminalImportStatus,
  validateImportFile,
} from '../model/dataInputActions';
import {
  DATA_INPUT_YEARS,
  ACTIVE_RECALCULATION_STATUSES,
  DATA_SOURCE_IDS,
  TERMINAL_RECALCULATION_STATUSES,
  createEmptyDataInputState,
  getScope3AttentionCount,
  getScopeAttentionCount,
  isDataInputRecoveryRequired,
  normalizeActiveImports,
  normalizeDataInputOverview,
  normalizeDataInputRecoveryState,
  normalizeHistory,
  normalizeImportOperation,
  normalizeRecalculationOperation,
} from '../model/dataInputModel';
import {
  importOperationError,
  recalculationOperationError,
  waitForImport,
  waitForRecalculation,
} from './dataInputPolling';

const CAT19_CALCULATION_STATUS = Object.freeze({
  CALCULATED: 'CALCULATED',
  PENDING_EF: 'PENDING_EF',
});
const IMPORT_SOURCE_IDS = [
  DATA_SOURCE_IDS.SCOPE_1_FUEL,
  DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY,
  DATA_SOURCE_IDS.SCOPE_3_AF_REPORT,
  DATA_SOURCE_IDS.SCOPE_3_EWASTE,
];
const TRANSIENT_SEAT_REQUEST_STATUSES = new Set([0, 408, 429]);

const EMPTY_SCOPE3_AF_SNAPSHOT = Object.freeze({
  snapshotId: null,
  operationId: null,
  files: [],
});

const normalizeScope3AfSnapshot = (payload) => ({
  snapshotId: payload?.snapshot_id || null,
  operationId: payload?.operation_id || null,
  files: Array.isArray(payload?.files) ? payload.files : [],
});

async function loadRecoveryDiagnostics(fiscalYear, signal) {
  const [importHistory, recalculationHistory] = await Promise.allSettled([
    getDataInputImportHistory(fiscalYear, { signal }),
    getDataRecalculationHistory(fiscalYear, { signal }),
  ]);
  return normalizeDataInputRecoveryState(
    importHistory.status === 'fulfilled' ? importHistory.value : null,
    recalculationHistory.status === 'fulfilled' ? recalculationHistory.value : [],
    fiscalYear,
  );
}

const seatRequestFingerprint = (fiscalYear, payload) => JSON.stringify({
  fiscalYear: Number(fiscalYear),
  mode: payload.mode,
  asOfDate: payload.asOfDate || null,
  reason: payload.reason || '',
  totalSeats: payload.mode === 'manual' ? payload.totalSeats : null,
  workingDays: payload.mode === 'manual' ? payload.workingDays : null,
});

const shouldRetainSeatRequestIdentity = (error) => {
  const status = Number(error?.status ?? error?.response?.status ?? 0);
  return TRANSIENT_SEAT_REQUEST_STATUSES.has(status) || status >= 500;
};

const seatRequestStorageKey = (authenticatedUserId, fiscalYear) => (
  `data-input:${dataInputOperationKey(
    authenticatedUserId,
    DATA_SOURCE_IDS.SCOPE_3_SEAT_API,
    fiscalYear,
  )}:pending`
);

const loadSeatRequestIdentity = (authenticatedUserId, fiscalYear) => {
  try {
    const identity = JSON.parse(window.sessionStorage.getItem(
      seatRequestStorageKey(authenticatedUserId, fiscalYear),
    ));
    return typeof identity?.fingerprint === 'string'
      && typeof identity?.idempotencyKey === 'string'
      ? identity
      : null;
  } catch {
    return null;
  }
};

const storeSeatRequestIdentity = (authenticatedUserId, fiscalYear, identity) => {
  try {
    window.sessionStorage.setItem(
      seatRequestStorageKey(authenticatedUserId, fiscalYear),
      JSON.stringify(identity),
    );
  } catch {
    // The in-memory identity still protects retries while this page remains open.
  }
};

const removeSeatRequestIdentity = (authenticatedUserId, fiscalYear) => {
  try {
    window.sessionStorage.removeItem(
      seatRequestStorageKey(authenticatedUserId, fiscalYear),
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
};

const cat8RequestFingerprint = (fiscalYear, payload) => JSON.stringify({
  fiscalYear: Number(fiscalYear),
  mode: payload.mode,
  asOfDate: payload.asOfDate || null,
  reason: payload.reason || '',
});

const cat8RequestStorageKey = (authenticatedUserId, fiscalYear) => (
  `data-input:${dataInputOperationKey(
    authenticatedUserId,
    DATA_SOURCE_IDS.SCOPE_3_CAT8,
    fiscalYear,
  )}:pending`
);

const loadCat8RequestIdentity = (authenticatedUserId, fiscalYear) => {
  try {
    const identity = JSON.parse(window.sessionStorage.getItem(
      cat8RequestStorageKey(authenticatedUserId, fiscalYear),
    ));
    return typeof identity?.fingerprint === 'string'
      && typeof identity?.idempotencyKey === 'string'
      ? identity
      : null;
  } catch {
    return null;
  }
};

const storeCat8RequestIdentity = (authenticatedUserId, fiscalYear, identity) => {
  try {
    window.sessionStorage.setItem(
      cat8RequestStorageKey(authenticatedUserId, fiscalYear),
      JSON.stringify(identity),
    );
  } catch {
    // The in-memory identity still protects retries while this page remains open.
  }
};

const removeCat8RequestIdentity = (authenticatedUserId, fiscalYear) => {
  try {
    window.sessionStorage.removeItem(
      cat8RequestStorageKey(authenticatedUserId, fiscalYear),
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
};

const importFileFingerprint = (file) => {
  if (!file) return '';
  return JSON.stringify({
    name: String(file.name || ''),
    size: Number(file.size) || 0,
    lastModified: Number(file.lastModified) || 0,
  });
};

const importFilesFingerprint = (files) => JSON.stringify(
  (Array.isArray(files) ? files : [])
    .filter(Boolean)
    .map((file) => JSON.parse(importFileFingerprint(file)))
    .sort((left, right) => (
      left.name < right.name ? -1 : left.name > right.name ? 1 : 0
    )),
);

const importRequestStorageKey = (authenticatedUserId, sourceId, fiscalYear) => (
  `data-input:${dataInputOperationKey(
    authenticatedUserId,
    sourceId,
    fiscalYear,
  )}:pending`
);

const loadImportRequestIdentity = (authenticatedUserId, sourceId, fiscalYear) => {
  try {
    const identity = JSON.parse(window.sessionStorage.getItem(
      importRequestStorageKey(authenticatedUserId, sourceId, fiscalYear),
    ));
    return typeof identity?.fingerprint === 'string'
      && typeof identity?.idempotencyKey === 'string'
      ? identity
      : null;
  } catch {
    return null;
  }
};

const storeImportRequestIdentity = (
  authenticatedUserId,
  sourceId,
  fiscalYear,
  identity,
) => {
  try {
    window.sessionStorage.setItem(
      importRequestStorageKey(authenticatedUserId, sourceId, fiscalYear),
      JSON.stringify(identity),
    );
  } catch {
    // The in-memory identity still protects retries while this page remains open.
  }
};

const removeImportRequestIdentity = (authenticatedUserId, sourceId, fiscalYear) => {
  try {
    window.sessionStorage.removeItem(
      importRequestStorageKey(authenticatedUserId, sourceId, fiscalYear),
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
};

const acquireImportRequestIdentity = (
  identitiesRef,
  authenticatedUserId,
  sourceId,
  fiscalYear,
  fingerprint,
) => {
  const identityKey = dataInputOperationKey(
    authenticatedUserId,
    sourceId,
    fiscalYear,
  );
  const current = identitiesRef.current[identityKey];
  if (current?.fingerprint === fingerprint) return current;
  const stored = loadImportRequestIdentity(
    authenticatedUserId,
    sourceId,
    fiscalYear,
  );
  if (stored?.fingerprint === fingerprint) {
    identitiesRef.current[identityKey] = stored;
    return stored;
  }
  const identity = { fingerprint, idempotencyKey: createIdempotencyKey() };
  identitiesRef.current[identityKey] = identity;
  storeImportRequestIdentity(
    authenticatedUserId,
    sourceId,
    fiscalYear,
    identity,
  );
  return identity;
};

const releaseImportRequestIdentity = (
  identitiesRef,
  authenticatedUserId,
  sourceId,
  fiscalYear,
  identity,
) => {
  const identityKey = dataInputOperationKey(
    authenticatedUserId,
    sourceId,
    fiscalYear,
  );
  if (identitiesRef.current[identityKey] !== identity) return;
  delete identitiesRef.current[identityKey];
  removeImportRequestIdentity(authenticatedUserId, sourceId, fiscalYear);
};

const withImportContext = (operation, sourceId, fiscalYear, fallbackFileName) => ({
  ...operation,
  fiscalYear,
  importType: sourceId,
  fileName: operation?.fileName || fallbackFileName || null,
});

export function cat19CalculationStatus(result, currentStatus) {
  const outcome = String(result?.recalculation?.outcome || '').toUpperCase();
  if (outcome === 'SUCCESS') return CAT19_CALCULATION_STATUS.CALCULATED;
  if (outcome === 'SKIPPED') return CAT19_CALCULATION_STATUS.PENDING_EF;
  return currentStatus;
}

export function dataInputOperationKey(authenticatedUserId, resourceGroup, fiscalYear) {
  const userNamespace = encodeURIComponent(String(authenticatedUserId || '').trim());
  if (!userNamespace) throw new Error('Authenticated user ID is required for data input.');
  return [userNamespace, resourceGroup, Number(fiscalYear)].join(':');
}

export function recalculationOperationKey(authenticatedUserId, operation) {
  const targets = [...(operation?.targets || [])].map(String).sort();
  const resourceGroup = targets.length > 0
    ? `recalculation:${targets.join('+')}`
    : `recalculation-operation:${operation?.id}`;
  return dataInputOperationKey(authenticatedUserId, resourceGroup, operation?.fiscalYear);
}

export default function useDataInputMaster() {
  const { auth } = useAuthContext();
  const authenticatedUserId = getAuthenticatedUserId(auth);
  const initialYear = reportingCalendarYear();
  const [year, setYearState] = useState(initialYear);
  const [data, setData] = useState(() => createEmptyDataInputState(initialYear));
  const [pendingFiles, setPendingFiles] = useState({});
  const [busySources, setBusySources] = useState({});
  const [operations, setOperations] = useState({});
  const [importMonitoringError, setImportMonitoringError] = useState(null);
  const [recoveredImportResult, setRecoveredImportResult] = useState(null);
  const [scope3AfSnapshot, setScope3AfSnapshot] = useState(EMPTY_SCOPE3_AF_SNAPSHOT);
  const [recalculating, setRecalculating] = useState(false);
  const [recalculationOperation, setRecalculationOperation] = useState(null);
  const [recalculationMonitoringError, setRecalculationMonitoringError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const yearRef = useRef(initialYear);
  const overviewSequence = useRef(0);
  const overviewController = useRef(null);
  const recalculationReadinessController = useRef(null);
  const activeImportsController = useRef(null);
  const activeImportsRef = useRef([]);
  const pollControllers = useRef(new Map());
  const recalculationControllers = useRef(new Map());
  const mountedRef = useRef(true);
  const authenticatedUserRef = useRef(authenticatedUserId);
  authenticatedUserRef.current = authenticatedUserId;
  const seatRequestIdentityRef = useRef(
    loadSeatRequestIdentity(authenticatedUserId, initialYear),
  );
  const cat8RequestIdentityRef = useRef(
    loadCat8RequestIdentity(authenticatedUserId, initialYear),
  );
  const importRequestIdentitiesRef = useRef({});

  const pendingKey = useCallback(
    (sourceId, targetYear = yearRef.current) => dataInputOperationKey(
      authenticatedUserId,
      sourceId,
      targetYear,
    ),
    [authenticatedUserId],
  );
  const recalculationKey = useCallback(
    operation => recalculationOperationKey(authenticatedUserId, operation),
    [authenticatedUserId],
  );

  const markBusy = useCallback((key, busy) => {
    if (!mountedRef.current) return;
    setBusySources((previous) => {
      if (busy) return { ...previous, [key]: true };
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, []);

  const setOperation = useCallback((key, operation) => {
    if (!mountedRef.current) return;
    setOperations((previous) => ({ ...previous, [key]: operation }));
  }, []);

  const loadRecalculationReadiness = useCallback(async (
    targetYear = yearRef.current,
  ) => {
    recalculationReadinessController.current?.abort();
    const controller = new AbortController();
    recalculationReadinessController.current = controller;
    const requestUserId = authenticatedUserId;
    const readiness = await getDataRecalculationReadiness(targetYear, {
      signal: controller.signal,
    });
    if (
      controller.signal.aborted
      || !mountedRef.current
      || yearRef.current !== targetYear
      || authenticatedUserRef.current !== requestUserId
    ) return null;
    const normalized = normalizeDataInputOverview(readiness, targetYear);
    setData((current) => ({
      ...current,
      recalculationImpact: normalized.recalculationImpact,
      activeRecalculations: normalized.activeRecalculations,
      lastRecalculation: normalized.lastRecalculation,
    }));
    if (recalculationReadinessController.current === controller) {
      recalculationReadinessController.current = null;
    }
    return normalized;
  }, [authenticatedUserId]);

  const reload = useCallback(async (
    targetYear = yearRef.current,
    { silent = false } = {},
  ) => {
    const requestSequence = overviewSequence.current + 1;
    overviewSequence.current = requestSequence;
    overviewController.current?.abort();
    const controller = new AbortController();
    overviewController.current = controller;
    const requestUserId = authenticatedUserId;
    const isCurrentRequest = () => (
      !controller.signal.aborted
      && overviewSequence.current === requestSequence
      && yearRef.current === targetYear
      && authenticatedUserRef.current === requestUserId
    );

    if (!silent && yearRef.current === targetYear) {
      setLoading(true);
      setError(null);
    }
    try {
      const overview = await getDataInputOverview(targetYear, { signal: controller.signal });
      if (!isCurrentRequest()) return;
      const normalized = normalizeDataInputOverview(overview, targetYear);
      activeImportsRef.current = normalized.activeImports;
      setData(normalized);
    } catch (loadError) {
      if (!isCurrentRequest()) return;
      if (isDataInputRecoveryRequired(loadError)) {
        const diagnostics = await loadRecoveryDiagnostics(
          targetYear,
          controller.signal,
        );
        if (!isCurrentRequest()) return;
        activeImportsRef.current = [];
        setData(diagnostics);
        setError(loadError);
      } else if (!silent) {
        const empty = createEmptyDataInputState(targetYear);
        activeImportsRef.current = [];
        setData(empty);
        setError(loadError);
      }
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
      }
    }
  }, [authenticatedUserId]);

  const loadImportHistory = useCallback(async (
    targetYear = yearRef.current,
    sourceId = null,
  ) => {
    const payload = await getDataInputImportHistory(
      targetYear,
      sourceId ? { params: { import_type: sourceId } } : undefined,
    );
    return Object.fromEntries(
      Object.entries(payload?.sources || {}).map(([sourceId, source]) => [
        sourceId,
        {
          ...source,
          history: normalizeHistory(source?.history || [], sourceId),
        },
      ]),
    );
  }, []);

  const loadDriverHistory = useCallback(async (targetYear = yearRef.current) => {
    const payload = await getDataInputDriverHistory(targetYear);
    return Object.fromEntries(
      Object.entries(payload?.sources || {}).map(([sourceId, source]) => [
        sourceId,
        {
          ...source,
          history: normalizeHistory(source?.history || [], sourceId),
        },
      ]),
    );
  }, []);

  const pollActiveImports = useCallback(async () => {
    activeImportsController.current?.abort();
    const controller = new AbortController();
    activeImportsController.current = controller;
    try {
      const payload = await getActiveDataImports({ signal: controller.signal });
      if (controller.signal.aborted || !mountedRef.current) return;
      const next = normalizeActiveImports(payload?.active_imports);
      activeImportsRef.current = next;
      setData((current) => ({ ...current, activeImports: next }));
      return next;
    } catch {
      // Overview and the local operation poll remain authoritative. A
      // transient lightweight-status failure must not blank the page.
    }
    return null;
  }, []);

  useEffect(() => {
    let isCurrentYear = true;
    reload(year).then(() => {
      if (isCurrentYear) pollActiveImports();
    });
    return () => {
      isCurrentYear = false;
    };
  }, [pollActiveImports, reload, year]);

  useEffect(() => {
    mountedRef.current = true;
    const controllers = pollControllers.current;
    const activeRecalculationControllers = recalculationControllers.current;
    const readinessController = recalculationReadinessController;
    return () => {
      mountedRef.current = false;
      overviewController.current?.abort();
      readinessController.current?.abort();
      activeImportsController.current?.abort();
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
      activeRecalculationControllers.forEach((controller) => controller.abort());
      activeRecalculationControllers.clear();
    };
  }, []);

  const setYear = useCallback((nextYear) => {
    const normalizedYear = Number(nextYear);
    if (normalizedYear === yearRef.current) return;
    yearRef.current = normalizedYear;
    activeImportsController.current?.abort();
    pollControllers.current.forEach((controller) => controller.abort());
    pollControllers.current.clear();
    recalculationControllers.current.forEach((controller) => controller.abort());
    recalculationControllers.current.clear();
    recalculationReadinessController.current?.abort();
    setRecalculating(false);
    setRecalculationOperation(null);
    setRecalculationMonitoringError(null);
    setImportMonitoringError(null);
    setRecoveredImportResult(null);
    setScope3AfSnapshot(EMPTY_SCOPE3_AF_SNAPSHOT);
    setBusySources({});
    setOperations({});
    seatRequestIdentityRef.current = loadSeatRequestIdentity(
      authenticatedUserId,
      normalizedYear,
    );
    cat8RequestIdentityRef.current = loadCat8RequestIdentity(
      authenticatedUserId,
      normalizedYear,
    );
    setYearState(normalizedYear);
    setError(null);
  }, [authenticatedUserId]);

  const setPendingFile = useCallback((sourceId, file) => {
    const key = pendingKey(sourceId);
    if (!file) {
      setPendingFiles((previous) => ({ ...previous, [key]: null }));
      return { valid: true, code: null };
    }
    const validation = validateImportFile(file);
    setPendingFiles((previous) => ({
      ...previous,
      [key]: validation.valid ? file : null,
    }));
    return validation;
  }, [pendingKey]);

  const monitorImportOperation = useCallback(async ({
    operation, sourceId, targetYear, key, file,
  }) => {
    const controller = new AbortController();
    pollControllers.current.get(key)?.abort();
    pollControllers.current.set(key, controller);
    let shouldRetainOperation = false;
    if (mountedRef.current) setImportMonitoringError(null);
    try {
      const completedStatus = isTerminalImportStatus(operation.status)
        ? operation
        : await waitForImport(operation.id, {
          signal: controller.signal,
          initialOperation: operation,
          onProgress: (latest) => {
            if (!isActiveImportStatus(latest?.status)) return;
            setOperation(
              key,
              withImportContext(latest, sourceId, targetYear, file?.name || operation.fileName),
            );
          },
        });
      const completed = completedStatus
        ? withImportContext(
          completedStatus,
          sourceId,
          targetYear,
          file?.name || operation.fileName,
        )
        : null;
      if (!completed) return { kind: 'stale' };
      if (activeImportsRef.current.some(({ id }) => id === completed.id)) {
        activeImportsRef.current = activeImportsRef.current.filter(
          ({ id }) => id !== completed.id,
        );
        if (mountedRef.current) {
          setData((current) => ({
            ...current,
            activeImports: current.activeImports.filter(({ id }) => id !== completed.id),
          }));
        }
      }
      const failedOperation = completed.status === 'SUCCESS'
        ? null
        : importOperationError(completed);
      if (!failedOperation) {
        setPendingFiles((previous) => ({ ...previous, [key]: null }));
      }
      if (yearRef.current === targetYear) {
        await reload(targetYear, { silent: true });
      }
      if (failedOperation) throw failedOperation;
      return { kind: 'success', operation: completed, file };
    } catch (actionError) {
      if (!actionError?.isMonitoringError) throw actionError;
      shouldRetainOperation = true;
      const retainedOperation = withImportContext({
        ...operation,
        ...actionError.operation,
      }, sourceId, targetYear, file?.name || operation.fileName);
      setOperation(key, retainedOperation);
      if (mountedRef.current) {
        setImportMonitoringError(actionError);
      }
      return {
        kind: 'monitoring-error',
        operation: retainedOperation,
        error: actionError,
        file,
      };
    } finally {
      if (pollControllers.current.get(key) === controller) {
        if (!shouldRetainOperation) setOperation(key, null);
        pollControllers.current.delete(key);
        markBusy(key, false);
      }
    }
  }, [markBusy, reload, setOperation]);

  useEffect(() => {
    data.activeImports.forEach((operation) => {
      if (!operation?.id
        || !operation.importType
        || !isActiveImportStatus(operation.status)) return;

      const targetYear = operation.fiscalYear ?? year;
      const key = pendingKey(operation.importType, targetYear);
      if (pollControllers.current.has(key)) return;

      markBusy(key, true);
      setOperation(key, operation);
      setRecoveredImportResult(null);
      monitorImportOperation({
        operation,
        sourceId: operation.importType,
        targetYear,
        key,
        file: null,
      }).then((monitorResult) => {
        const terminalOperation = monitorResult?.operation;
        if (mountedRef.current && isTerminalImportStatus(terminalOperation?.status)) {
          setRecoveredImportResult(terminalOperation);
        }
      }).catch((actionError) => {
        const terminalOperation = actionError?.operation;
        if (mountedRef.current && isTerminalImportStatus(terminalOperation?.status)) {
          setRecoveredImportResult(terminalOperation);
        }
        // Status-fetch failures are represented by importMonitoringError. They
        // never create a terminal result or invent FAILED.
      });
    });
  }, [
    data.activeImports, markBusy, monitorImportOperation, pendingKey, setOperation, year,
  ]);

  const uploadSource = useCallback(async (sourceId) => {
    const targetYear = yearRef.current;
    const key = pendingKey(sourceId, targetYear);
    const file = pendingFiles[key];
    if (!file) return null;

    const validation = validateImportFile(file);
    if (!validation.valid) {
      const validationError = new Error(validation.code);
      validationError.code = validation.code;
      throw validationError;
    }

    markBusy(key, true);
    const queuedOperation = {
      id: null,
      fiscalYear: targetYear,
      importType: sourceId,
      fileName: file.name,
      status: 'QUEUED',
      message: '',
      counters: {},
    };
    setOperation(key, queuedOperation);
    const requestIdentity = acquireImportRequestIdentity(
      importRequestIdentitiesRef,
      authenticatedUserId,
      sourceId,
      targetYear,
      importFileFingerprint(file),
    );
    let didStartMonitoring = false;
    try {
      const accepted = await createDataImport({
        fiscalYear: targetYear,
        importType: sourceId,
        file,
        idempotencyKey: requestIdentity.idempotencyKey,
      });
      releaseImportRequestIdentity(
        importRequestIdentitiesRef,
        authenticatedUserId,
        sourceId,
        targetYear,
        requestIdentity,
      );
      const importId = accepted.id || accepted.operation_id;
      if (!importId) throw new Error('Import response did not include an operation ID.');

      const acceptedOperation = {
        ...queuedOperation,
        ...normalizeImportOperation(accepted),
        id: importId,
        fiscalYear: targetYear,
        importType: sourceId,
        fileName: file.name,
      };
      if (isActiveImportStatus(acceptedOperation.status)) {
        setOperation(key, acceptedOperation);
      }
      didStartMonitoring = true;
      return await monitorImportOperation({
        operation: acceptedOperation,
        sourceId,
        targetYear,
        key,
        file,
      });
    } catch (error) {
      if (!shouldRetainSeatRequestIdentity(error)) {
        releaseImportRequestIdentity(
          importRequestIdentitiesRef,
          authenticatedUserId,
          sourceId,
          targetYear,
          requestIdentity,
        );
      }
      throw error;
    } finally {
      if (!didStartMonitoring) {
        setOperation(key, null);
        markBusy(key, false);
      }
    }
  }, [authenticatedUserId, markBusy, monitorImportOperation, pendingFiles, pendingKey, setOperation]);

  const loadScope3AfSnapshot = useCallback(async (targetYear = yearRef.current) => {
    const payload = await getScope3AfSnapshot(targetYear);
    if (mountedRef.current) {
      setScope3AfSnapshot(normalizeScope3AfSnapshot(payload));
    }
    return normalizeScope3AfSnapshot(payload);
  }, []);

  const submitScope3AfSnapshot = useCallback(async ({
    fiscalYear,
    expectedSnapshotId,
    retainedFileIds,
    files,
  }) => {
    const targetYear = Number(fiscalYear) || yearRef.current;
    const sourceId = DATA_SOURCE_IDS.SCOPE_3_AF_REPORT;
    const key = pendingKey(sourceId, targetYear);
    const localFiles = Array.isArray(files) ? files : [];

    markBusy(key, true);
    const queuedOperation = {
      id: null,
      fiscalYear: targetYear,
      importType: sourceId,
      fileName: localFiles[0]?.name || 'Scope 3 AF snapshot',
      status: 'QUEUED',
      message: '',
      counters: {},
    };
    setOperation(key, queuedOperation);
    const requestIdentity = acquireImportRequestIdentity(
      importRequestIdentitiesRef,
      authenticatedUserId,
      sourceId,
      targetYear,
      JSON.stringify({
        retainedFileIds: [...(retainedFileIds || [])].map(String).sort(),
        files: JSON.parse(importFilesFingerprint(localFiles)),
      }),
    );
    let didStartMonitoring = false;
    try {
      const accepted = await confirmScope3AfImport({
        fiscalYear: targetYear,
        expectedSnapshotId: expectedSnapshotId || '',
        retainedFileIds: retainedFileIds || [],
        files: localFiles,
        idempotencyKey: requestIdentity.idempotencyKey,
      });
      releaseImportRequestIdentity(
        importRequestIdentitiesRef,
        authenticatedUserId,
        sourceId,
        targetYear,
        requestIdentity,
      );
      const importId = accepted.id || accepted.operation_id;
      if (!importId) throw new Error('Import response did not include an operation ID.');

      const acceptedOperation = {
        ...queuedOperation,
        ...normalizeImportOperation(accepted),
        id: importId,
        fiscalYear: targetYear,
        importType: sourceId,
        fileName: localFiles[0]?.name || accepted.file_name || 'Scope 3 AF snapshot',
      };
      if (isActiveImportStatus(acceptedOperation.status)) {
        setOperation(key, acceptedOperation);
      }
      didStartMonitoring = true;
      return await monitorImportOperation({
        operation: acceptedOperation,
        sourceId,
        targetYear,
        key,
        file: null,
      });
    } catch (error) {
      if (!shouldRetainSeatRequestIdentity(error)) {
        releaseImportRequestIdentity(
          importRequestIdentitiesRef,
          authenticatedUserId,
          sourceId,
          targetYear,
          requestIdentity,
        );
      }
      throw error;
    } finally {
      if (!didStartMonitoring) {
        setOperation(key, null);
        markBusy(key, false);
      }
    }
  }, [authenticatedUserId, markBusy, monitorImportOperation, pendingKey, setOperation]);

  const retryImportStatus = useCallback(async (operation) => {
    if (!operation?.id || !operation?.importType) return null;
    const targetYear = operation.fiscalYear ?? yearRef.current;
    const sourceId = operation.importType;
    const key = pendingKey(sourceId, targetYear);
    const file = pendingFiles[key] || null;
    markBusy(key, true);
    if (isActiveImportStatus(operation.status)) setOperation(key, operation);
    return monitorImportOperation({
      operation,
      sourceId,
      targetYear,
      key,
      file,
    });
  }, [markBusy, monitorImportOperation, pendingFiles, pendingKey, setOperation]);

  const cancelActiveImport = useCallback(async (operationId = null) => {
    const localEntry = Object.entries(operations)
      .find(([, currentOperation]) => currentOperation?.id === operationId);
    const recoveredOperation = activeImportsRef.current
      .find(({ id }) => id === operationId);
    const operation = localEntry?.[1] || recoveredOperation;
    const targetId = operationId || operation?.id;
    if (!canCancelKnownImport(operation, operations, targetId)) return null;
    if (localEntry) {
      const [localKey] = localEntry;
      const controller = pollControllers.current.get(localKey);
      if (controller) {
        pollControllers.current.delete(localKey);
        controller.abort();
      }
    }
    const cancelled = normalizeImportOperation(await cancelDataImport(targetId));
    activeImportsRef.current = activeImportsRef.current.filter(({ id }) => id !== targetId);
    setData((current) => ({
      ...current,
      activeImports: current.activeImports.filter(({ id }) => id !== targetId),
    }));
    if (localEntry) {
      const [localKey] = localEntry;
      setOperation(localKey, null);
      markBusy(localKey, false);
    }
    await reload(yearRef.current, { silent: true });
    return cancelled;
  }, [markBusy, operations, reload, setOperation]);

  const submitSeatDriver = useCallback(async (payload) => {
    const targetYear = yearRef.current;
    const sourceId = DATA_SOURCE_IDS.SCOPE_3_SEAT_API;
    const key = pendingKey(sourceId, targetYear);
    const fingerprint = seatRequestFingerprint(targetYear, payload);
    if (seatRequestIdentityRef.current?.fingerprint !== fingerprint) {
      seatRequestIdentityRef.current = {
        fingerprint,
        idempotencyKey: createIdempotencyKey(),
      };
      storeSeatRequestIdentity(
        authenticatedUserId,
        targetYear,
        seatRequestIdentityRef.current,
      );
    }
    const requestIdentity = seatRequestIdentityRef.current;
    let mutationAccepted = false;
    markBusy(key, true);
    try {
      const result = payload.mode === 'manual'
        ? await createSeatManualInput(targetYear, payload, requestIdentity.idempotencyKey)
        : await syncSeatDriver(targetYear, payload, requestIdentity.idempotencyKey);
      mutationAccepted = true;
      if (mountedRef.current && yearRef.current === targetYear) await reload(targetYear);
      if (seatRequestIdentityRef.current === requestIdentity) {
        seatRequestIdentityRef.current = null;
        removeSeatRequestIdentity(authenticatedUserId, targetYear);
      }
      return result;
    } catch (error) {
      if (
        seatRequestIdentityRef.current === requestIdentity
        && !mutationAccepted
        && !shouldRetainSeatRequestIdentity(error)
      ) {
        seatRequestIdentityRef.current = null;
        removeSeatRequestIdentity(authenticatedUserId, targetYear);
      }
      throw error;
    } finally {
      markBusy(key, false);
    }
  }, [authenticatedUserId, markBusy, pendingKey, reload]);

  const submitCat8Driver = useCallback(async (payload) => {
    const targetYear = yearRef.current;
    const sourceId = DATA_SOURCE_IDS.SCOPE_3_CAT8;
    const key = pendingKey(sourceId, targetYear);
    const fingerprint = cat8RequestFingerprint(targetYear, payload);
    if (cat8RequestIdentityRef.current?.fingerprint !== fingerprint) {
      cat8RequestIdentityRef.current = {
        fingerprint,
        idempotencyKey: createIdempotencyKey(),
      };
      storeCat8RequestIdentity(
        authenticatedUserId,
        targetYear,
        cat8RequestIdentityRef.current,
      );
    }
    const requestIdentity = cat8RequestIdentityRef.current;
    let mutationAccepted = false;
    markBusy(key, true);
    try {
      const result = payload.mode === 'manual'
        ? await createCat8ManualInput(
          targetYear,
          payload,
          requestIdentity.idempotencyKey,
        )
        : await syncCat8Driver(targetYear, payload, requestIdentity.idempotencyKey);
      mutationAccepted = true;
      if (cat8RequestIdentityRef.current === requestIdentity) {
        cat8RequestIdentityRef.current = null;
        removeCat8RequestIdentity(authenticatedUserId, targetYear);
      }
      if (mountedRef.current && yearRef.current === targetYear) await reload(targetYear);
      return result;
    } catch (error) {
      if (
        cat8RequestIdentityRef.current === requestIdentity
        && !mutationAccepted
        && !shouldRetainSeatRequestIdentity(error)
      ) {
        cat8RequestIdentityRef.current = null;
        removeCat8RequestIdentity(authenticatedUserId, targetYear);
      }
      throw error;
    } finally {
      markBusy(key, false);
    }
  }, [authenticatedUserId, markBusy, pendingKey, reload]);

  const loadDriverSnapshot = useCallback((snapshotId, config) => (
    getDriverSnapshot(snapshotId, config)
  ), []);

  const saveOutsourcing = useCallback(async (payload) => {
    const targetYear = yearRef.current;
    const sourceId = DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING;
    const key = pendingKey(sourceId, targetYear);
    markBusy(key, true);
    try {
      const result = await saveCat19Outsourcing(targetYear, {
        amount_jpy: payload.amountJpy,
        note: payload.note,
      });
      if (mountedRef.current && yearRef.current === targetYear) {
        setData((previous) => ({
          ...previous,
          sources: {
            ...previous.sources,
            [sourceId]: {
              ...previous.sources[sourceId],
              amountJpy: Number(result.amount_jpy ?? payload.amountJpy),
              note: result.note ?? payload.note,
              updatedAt: result.updated_at || previous.sources[sourceId].updatedAt,
              calculationStatus: cat19CalculationStatus(
                result,
                previous.sources[sourceId].calculationStatus,
              ),
            },
          },
        }));
      }
      if (mountedRef.current && yearRef.current === targetYear) await reload(targetYear);
      return result;
    } finally {
      markBusy(key, false);
    }
  }, [markBusy, pendingKey, reload]);

  const monitorRecalculationOperation = useCallback(async ({
    targetYear, operation, controller,
  }) => {
    const operationKey = recalculationKey(operation);
    if (mountedRef.current) {
      setRecalculationMonitoringError((current) => (
        current?.operationId === operation.id ? null : current
      ));
    }
    try {
      const completed = TERMINAL_RECALCULATION_STATUSES.has(operation.status)
        ? operation
        : await waitForRecalculation(targetYear, operation.id, {
          signal: controller.signal,
          initialOperation: operation,
          onStatus: (latestOperation) => {
            if (!mountedRef.current || yearRef.current !== targetYear) return;
            setRecalculationOperation(latestOperation);
            setRecalculationMonitoringError((current) => (
              current?.operationId === operation.id ? null : current
            ));
          },
        });
      if (!completed || controller.signal.aborted) return null;
      if (TERMINAL_RECALCULATION_STATUSES.has(completed.status)
        && yearRef.current === targetYear) {
        await reload(targetYear, { silent: true });
      }
      if (mountedRef.current) {
        setData((current) => ({
          ...current,
          activeRecalculations: current.activeRecalculations.filter(
            ({ id }) => id !== completed.id,
          ),
        }));
        if (yearRef.current === targetYear) setRecalculationOperation(completed);
      }
      if (recalculationControllers.current.get(operationKey) === controller) {
        recalculationControllers.current.delete(operationKey);
      }
      return completed;
    } catch (monitoringError) {
      if (controller.signal.aborted) return null;
      const statusError = monitoringError?.isMonitoringError
        ? monitoringError
        : Object.assign(new Error('Status is not available now. The process may still be running.'), {
          code: 'RECALCULATION_STATUS_UNAVAILABLE',
          isMonitoringError: true,
          operationId: operation.id,
          operation,
          cause: monitoringError,
        });
      if (mountedRef.current && yearRef.current === targetYear) {
        setRecalculationMonitoringError(statusError);
      }
      return null;
    }
  }, [recalculationKey, reload]);

  const recalculate = useCallback(async (payload) => {
    const targetYear = yearRef.current;
    const controller = new AbortController();
    let trackedOperationKey = null;
    setRecalculating(true);
    setRecalculationMonitoringError(null);
    try {
      const accepted = normalizeRecalculationOperation(
        await runDataRecalculation(targetYear, payload, createIdempotencyKey()),
      );
      if (!accepted?.id) throw new Error('Recalculation response did not include an operation ID.');
      trackedOperationKey = recalculationKey(accepted);
      recalculationControllers.current.set(trackedOperationKey, controller);
      if (mountedRef.current) setRecalculationOperation(accepted);
      const completed = await monitorRecalculationOperation({
        targetYear,
        operation: accepted,
        controller,
      });
      if (!completed) return null;
      if (completed.status === 'FAILED') throw recalculationOperationError(completed);
      return completed;
    } catch (actionError) {
      if (controller.signal.aborted) return null;
      throw actionError;
    } finally {
      if (trackedOperationKey
        && recalculationControllers.current.get(trackedOperationKey) === controller) {
        recalculationControllers.current.delete(trackedOperationKey);
      }
      if (mountedRef.current) {
        setRecalculating(false);
      }
    }
  }, [monitorRecalculationOperation, recalculationKey]);

  const retryRecalculationStatus = useCallback(async () => {
    const monitoringOperation = recalculationMonitoringError?.operation;
    const currentMonitoringOperation = monitoringOperation?.fiscalYear === yearRef.current
      && ACTIVE_RECALCULATION_STATUSES.has(monitoringOperation.status)
      ? monitoringOperation
      : null;
    const overviewOperation = data.activeRecalculations.find((operation) => (
      operation.fiscalYear === yearRef.current
      && ACTIVE_RECALCULATION_STATUSES.has(operation.status)
    ));
    const localOperation = recalculationOperation?.fiscalYear === yearRef.current
      && ACTIVE_RECALCULATION_STATUSES.has(recalculationOperation.status)
      ? recalculationOperation
      : null;
    const operation = currentMonitoringOperation || localOperation || overviewOperation;
    if (!operation?.id || !ACTIVE_RECALCULATION_STATUSES.has(operation.status)) return operation;

    const operationKey = recalculationKey(operation);
    recalculationControllers.current.get(operationKey)?.abort();
    const controller = new AbortController();
    recalculationControllers.current.set(operationKey, controller);
    try {
      const completed = await monitorRecalculationOperation({
        targetYear: yearRef.current,
        operation,
        controller,
      });
      return completed;
    } finally {
      if (recalculationControllers.current.get(operationKey) === controller) {
        recalculationControllers.current.delete(operationKey);
      }
    }
  }, [
    data.activeRecalculations,
    monitorRecalculationOperation,
    recalculationMonitoringError,
    recalculationOperation,
    recalculationKey,
  ]);

  useEffect(() => {
    data.activeRecalculations.forEach((operation) => {
      const operationKey = recalculationKey(operation);
      if (!ACTIVE_RECALCULATION_STATUSES.has(operation?.status)
        || recalculationControllers.current.has(operationKey)) return;
      const targetYear = operation.fiscalYear;
      const controller = new AbortController();
      recalculationControllers.current.set(operationKey, controller);
      if (targetYear === year) setRecalculationOperation(operation);
      monitorRecalculationOperation({
        targetYear,
        operation,
        controller,
      }).then((completed) => {
      }).catch(() => {
        // The backend operation remains authoritative. A manual status retry is
        // available if an unexpected client-side monitoring error occurs.
      }).finally(() => {
        if (recalculationControllers.current.get(operationKey) === controller) {
          recalculationControllers.current.delete(operationKey);
        }
      });
    });
  }, [data.activeRecalculations, monitorRecalculationOperation, recalculationKey, year]);

  const deleteSource = useCallback(async (payload) => {
    const targetYear = yearRef.current;
    const key = pendingKey(payload.source, targetYear);
    markBusy(key, true);
    try {
      const result = await deleteDataInput(targetYear, payload);
      if (mountedRef.current && yearRef.current === targetYear) await reload(targetYear);
      return result;
    } finally {
      markBusy(key, false);
    }
  }, [markBusy, pendingKey, reload]);

  const pendingFor = useCallback(
    (sourceId) => pendingFiles[pendingKey(sourceId)] || null,
    [pendingFiles, pendingKey],
  );

  const operationFor = useCallback(
    (sourceId) => {
      const localOperation = operations[pendingKey(sourceId)];
      if (localOperation) return localOperation;
      return data.activeImports.find((operation) => (
        operation.importType === sourceId
        && operation.fiscalYear === yearRef.current
      )) || null;
    },
    [data.activeImports, operations, pendingKey],
  );

  const isSourceBusy = useCallback((sourceId) => {
    const key = pendingKey(sourceId);
    const localOperation = operations[key];
    const isCurrentActiveSource = data.activeImports.some((operation) => (
      operation.importType === sourceId
      && operation.fiscalYear === yearRef.current
      && isActiveImportStatus(operation.status)
    ));
    return Boolean(busySources[key])
      || isActiveImportStatus(localOperation?.status)
      || isCurrentActiveSource;
  }, [busySources, data.activeImports, operations, pendingKey]);

  const localActiveImports = useMemo(() => (
    IMPORT_SOURCE_IDS.reduce((activeOperations, sourceId) => {
      const key = pendingKey(sourceId);
      const operation = operations[key];
      if (busySources[key] || isActiveImportStatus(operation?.status)) {
        activeOperations.push({
          ...operation,
          fiscalYear: operation?.fiscalYear ?? year,
          importType: operation?.importType || sourceId,
          status: operation?.status || 'QUEUED',
        });
      }
      return activeOperations;
    }, [])
  ), [busySources, operations, pendingKey, year]);

  const currentRecoveredImports = data.activeImports.filter((operation) => (
    operation.fiscalYear === year && isActiveImportStatus(operation.status)
  ));
  const currentActiveImports = [...currentRecoveredImports];
  localActiveImports.forEach((operation) => {
    if (!currentActiveImports.some(({ id }) => id && id === operation.id)) {
      currentActiveImports.push(operation);
    }
  });
  const currentRecalculationOperation = recalculationOperation?.fiscalYear === year
    ? recalculationOperation
    : null;
  const currentActiveRecalculation = data.activeRecalculations.find((operation) => (
    operation.fiscalYear === year
    && ACTIVE_RECALCULATION_STATUSES.has(operation.status)
  )) || null;
  const currentOverviewRecalculation = data.lastRecalculation?.fiscalYear === year
    ? data.lastRecalculation
    : null;
  const currentMonitoringRecalculation = recalculationMonitoringError?.operation
    ?.fiscalYear === year
    ? recalculationMonitoringError.operation
    : null;
  const visibleRecalculationOperation = currentMonitoringRecalculation
    || currentActiveRecalculation
    || currentRecalculationOperation
    || currentOverviewRecalculation;
  const isRecalculationProcessing = Boolean(
    recalculating
    || currentActiveRecalculation
    || ACTIVE_RECALCULATION_STATUSES.has(visibleRecalculationOperation?.status),
  );

  const scope1AttentionCount = useMemo(() => getScopeAttentionCount(
    data,
    'scope1',
    data.sources[DATA_SOURCE_IDS.SCOPE_1_FUEL]?.currentName ? 0 : 1,
  ), [data]);
  const scope2AttentionCount = useMemo(() => getScopeAttentionCount(
    data,
    'scope2',
    data.sources[DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]?.currentName ? 0 : 1,
  ), [data]);
  const scope3AttentionCount = useMemo(() => getScope3AttentionCount(data), [data]);

  return {
    years: DATA_INPUT_YEARS,
    year,
    setYear,
    data,
    loading,
    error,
    reload,
    loadImportHistory,
    loadDriverHistory,
    loadRecalculationReadiness,
    pendingFor,
    setPendingFile,
    uploadSource,
    scope3AfSnapshot,
    loadScope3AfSnapshot,
    submitScope3AfSnapshot,
    importMonitoringError,
    recoveredImportResult,
    retryImportStatus,
    cancelActiveImport,
    submitSeatDriver,
    submitCat8Driver,
    loadDriverSnapshot,
    saveOutsourcing,
    recalculate,
    deleteSource,
    recalculating,
    isRecalculationProcessing,
    recalculationOperation: visibleRecalculationOperation,
    recalculationMonitoringError,
    retryRecalculationStatus,
    isSourceBusy,
    operationFor,
    activeImports: currentActiveImports,
    scope1AttentionCount,
    scope2AttentionCount,
    scope3AttentionCount,
  };
}
